<script setup lang="ts">
//#region imports
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from 'lodash-es';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useError } from '@/composables/useError';
import { isErrorResponse, type EmitsToListeners, type FormErrors } from '@/types/core';
import type {
  GetAffiliatesResponse,
  GetAffiliateOffersResponse,
  GetPlayersByIdResponse,
} from '@/types/openapi-ts';
//#endregion

//#region types
type PlayerPayload = GetPlayersByIdResponse['payload'];

interface Props {
  player?: Partial<PlayerPayload>;
  siteId?: string;
}

interface Emits {
  success: [];
  close: [];
}

export interface LinkAffiliateParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}

type ConfirmAction = 'attach' | 'detach';
type LinkAction = 'new' | 'update';
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  player: () => ({}),
  siteId: '',
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const popupManager = usePopupManager();
//#endregion

//#region state
const errors = ref<FormErrors>({});
const { getError } = useError({ errors });
const isLoading = ref(true);
const affiliateOptions = ref<GetAffiliatesResponse['payload']>([]);
const affiliateId = ref<string | null>(null);
const cloneAffiliateId = ref<string | null>(null);
const offerId = ref<string | null>(null);
const affiliateOffers = ref<GetAffiliateOffersResponse['payload']>([]);
const disabledOffer = ref(true);
const pinnedAffiliateId = ref<string | null>(null);
const pinnedAffiliateOfferId = ref<string | null>(null);
const action = ref<LinkAction>('new');
const limit = ref(33);
const searchQuery = ref('');
const selectLoading = ref(true);
const isVisible = ref(false);
//#endregion

//#region computed
const disabledLink = computed(() => {
  return (pinnedAffiliateId.value === affiliateId.value
    && pinnedAffiliateOfferId.value === offerId.value)
    || offerId.value === null;
});

const popupTitle = computed(() => {
  return t('players.pinPlayer.title', {player: props.player.site_player_id});
});

const topText = computed(() => {
  let type = 'edit';
  if (action.value === 'new') {
    type = 'new';
  }
  return t(`players.pinPlayer.${type}`, {player: props.player.site_player_id});
});
//#endregion

//#region methods
function getAffiliateOffers(id: string) {
  isLoading.value = true;
  return api
    .getAffiliateOffers(id)
    .then((res) => {
      affiliateOffers.value = props.siteId
        ? res.data.payload.filter((offer) =>
          offer.brand_visibility === 'all'
          || (offer.brand_visibility === 'only_selected'
            && offer.visible_brands.some((brand) => brand.site_id === props.siteId)))
        : res.data.payload;
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function getAffiliates(search?: string, nextLimit?: number) {
  selectLoading.value = true;
  return api
    .getAffiliates({
      search: search || '',
      limit: nextLimit || limit.value,
      sort_column: 'email',
      sort_dir: 'asc',
    })
    .then((res) => {
      affiliateOptions.value = res.data.payload;
    })
    .finally(() => {
      selectLoading.value = false;
    });
}

const getAffiliatesDebounced = debounce(getAffiliates, 800);

function searchMethod(v: string) {
  selectLoading.value = true;
  searchQuery.value = v;
  limit.value = 33;
  getAffiliatesDebounced(searchQuery.value, limit.value);
}

function changeAffiliate(id: string) {
  offerId.value = null;
  getAffiliateOffers(id).then(() => {
    disabledOffer.value = false;
  });
}

function clickConfirmHandler(confirmAction: ConfirmAction) {
  const currentAffiliate = affiliateOptions.value.find((item) => item.id === affiliateId.value);
  const email = currentAffiliate?.email || props.player?.affiliate_email || '';

  const popupParams = {
    attach: {
      title: t('players.confirm.attach.title'),
      message: t('players.confirm.attach.message', {from: props.player.site_player_id, to: email}),
      confirmTitle: t('players.confirm.attach.action'),
      confirmType: 'positive',
      confirmIcon: 'link',
      bodyIconColor: 'var(--text-positive)',
      successMethod: attach,
    },
    detach: {
      title: t('players.confirm.detach.title'),
      message: t('players.confirm.detach.message', {from: props.player.site_player_id, to: email}),
      confirmTitle: t('players.confirm.detach.action'),
      confirmType: 'negative',
      confirmIcon: 'unlink',
      bodyIconColor: 'var(--text-negative)',
      successMethod: detach,
    },
  };

  const confirmPopup = popupManager.confirm.open({
    data: {
      title: popupParams[confirmAction].title,
      message: popupParams[confirmAction].message,
      confirmTitle: popupParams[confirmAction].confirmTitle,
      confirmType: popupParams[confirmAction].confirmType,
      confirmIcon: popupParams[confirmAction].confirmIcon,
      bodyIconColor: popupParams[confirmAction].bodyIconColor,
      bodyIcon: 'exclamation-triangle',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await popupParams[confirmAction].successMethod();
        } catch (error) {
          window.console.error(error);
        } finally {
          context.handleClose();
        }
      },
    },
  });
}

function attach() {
  const playerId = props.player.id;
  if (!playerId || !offerId.value) {
    return;
  }

  return api.attachPlayerAffiliate(playerId, {
    affiliate_offer_id: offerId.value,
  }).then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    if (isErrorResponse(error)) {
      errors.value = error.data.errors;
    }
  });
}

function detach() {
  const playerId = props.player.id;
  if (!playerId) {
    return;
  }

  return api.detachPlayerAffiliate(playerId).then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    if (isErrorResponse(error)) {
      errors.value = error.data.errors;
    }
  });
}

function open() {
  pinnedAffiliateId.value = props.player.affiliate_id ?? null;
  pinnedAffiliateOfferId.value = props.player.affiliate_offer_id ?? null;

  if (props.player.affiliate_id !== null && props.player.affiliate_id !== undefined) {
    isLoading.value = true;
    action.value = 'update';
    disabledOffer.value = false;
    affiliateId.value = props.player.affiliate_id;
    cloneAffiliateId.value = props.player.affiliate_id;
    offerId.value = props.player.affiliate_offer_id ?? null;
    affiliateOptions.value = [
      {
        id: props.player.affiliate_id,
        email: props.player.affiliate_email,
      },
    ] as GetAffiliatesResponse['payload'];
    selectLoading.value = false;
    getAffiliateOffers(props.player.affiliate_id);
  } else {
    isLoading.value = false;
    selectLoading.value = false;
    action.value = 'new';
  }
  isVisible.value = true;
}

function handleClose() {
  isVisible.value = false;
  emit('close');
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="popupTitle"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiLabel
          :label="topText"
        />
        <div class="t:flex t:items-end t:gap-8">
          <div class="t:min-w-0 t:flex-1">
            <UiSelectElement
              v-model="affiliateId"
              :label="$t('players.pinPlayer.aff')"
              :placeholder="$t('players.pinPlayer.placeholder_aff')"
              :list="affiliateOptions"
              :loading="selectLoading"
              :remote-method="searchMethod"
              remote
              filterable
              size="big"
              option-label-param="email"
              option-name-param="email"
              :error="getError('affiliate_id')"
              @change="changeAffiliate"
            />
          </div>
          <UiButton
            v-if="action === 'update'"
            :disabled="cloneAffiliateId !== affiliateId"
            size="big"
            type="negative"
            icon="unlink"
            @click="clickConfirmHandler('detach')"
          >
            {{ $t('players.pinPlayer.unpin') }}
          </UiButton>
        </div>
        <UiSelectElement
          v-model="offerId"
          :label="$t('players.pinPlayer.offer')"
          :placeholder="$t('players.pinPlayer.placeholder_offer')"
          :disabled="disabledOffer"
          :list="affiliateOffers"
          filterable
          size="big"
          option-label-param="full_name"
          option-name-param="full_name"
          :error="getError('affiliate_offer_id')"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          size="big"
          type="negative"
          icon="times"
          @click="handleClose"
        >
          {{ $t('players.pinPlayer.cancel') }}
        </UiButton>
        <UiButton
          v-if="action === 'new'"
          :disabled="disabledLink"
          filled
          size="big"
          icon="link"
          @click="attach"
        >
          {{ $t('players.pinPlayer.pin_aff') }}
        </UiButton>
        <UiButton
          v-if="action === 'update'"
          :disabled="disabledLink"
          filled
          size="big"
          icon="check"
          @click="clickConfirmHandler('attach')"
        >
          {{ $t('players.pinPlayer.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
